import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from "react";
import { FaChevronLeft, FaChevronRight, FaTrash ,FaImages} from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import Swal from "sweetalert2";

const ITEMS_PER_PAGE = 10;

const ListLegal = () => {
  const [legals, setLegals] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedImage, setSelectedImage] = useState(null);
  const indexOfLast = currentPage * ITEMS_PER_PAGE;
  const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
  const currentPlans = legals.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(legals.length / ITEMS_PER_PAGE);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/delete_legal/${id}`,
          {
            method: "DELETE",
          }
        );

        const data = await response.json();

        if (response.ok && data.success) {
          const updatedList = legals.filter((member) => member._id !== id);
          setLegals(updatedList);

          const totalPagesAfterDelete = Math.ceil(
            updatedList.length / ITEMS_PER_PAGE
          );
          if (currentPage > totalPagesAfterDelete) {
            setCurrentPage(totalPagesAfterDelete);
          }

          Swal.fire({
            title: "Deleted!",
            text: "Legal has been deleted.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
          });
        } else {
          Swal.fire({
            title: "Error!",
            text: data.message || "Failed to delete Legal.",
            icon: "error",
          });
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire({
          title: "Error!",
          text: "Server error while deleting.",
          icon: "error",
        });
      }
    }
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const generatePagination = () => {
    const pages = [];

    if (totalPages <= 6) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }

    return pages;
  };

  const renderPageNumbers = () =>
    generatePagination().map((page, index) =>
      page === "..." ? (
        <span key={index} className="px-3 py-1 text-gray-500">
          ...
        </span>
      ) : (
        <button
          key={index}
          onClick={() => handlePageChange(page)}
          className={`px-3 py-1 rounded-full text-sm font-medium transition-all duration-300 ${
            currentPage === page
              ? "bg-blue-600 text-white"
              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
          }`}
        >
          {page}
        </button>
      )
    );

  useEffect(() => {
    const fetchTeamMembers = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/get_all_legal`
        );
        const data = await response.json();
        setLegals(data.data || []);
      } catch (error) {
        console.error("Error fetching Legal", error);
      }
    };

    fetchTeamMembers();
  }, []);

  const handlePrev = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  return (
    <div className="w-full bg-white">
      <div className="p-4 max-w-6xl mx-auto">
        <h2 className="text-2xl text-center font-semibold mb-7">
          Show Legal Documents
        </h2>

        <div className="overflow-x-auto shadow-md rounded-lg">
          <table className="min-w-full text-center bg-white border border-gray-200">
            <thead className="bg-gray-100 text-center ">
              <tr>
                <th className="px-4 py-2 border">S.No.</th>
                <th className="px-4 py-2 border">Image</th>
                <th className="px-4 py-2 border">Title</th>
                <th className="px-4 py-2 border">Certificate No</th>
                <th className="px-4 py-2 border">Issued Date</th>
                <th className="px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentPlans.length > 0 ? (
                currentPlans.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                    </td>
                    <td className="py-3 px-4">
                      <button onClick={() => setSelectedImage(item.photo)}>
                        <img
                          src={item.photo}
                          alt={item.title}
                          className="w-12 h-12 rounded-full object-cover hover:scale-105 transition"
                        />
                      </button>
                    </td>
                    <td className="px-4 py-2">{item.title}</td>
                    <td className="px-4 py-2">{item.certification_no}</td>
                    <td className="px-4 py-2">
                      {new Date(item.issue_date).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => handleDelete(item._id)}
                        className="text-red-600 hover:text-red-800 transition"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="14" className="py-6 text-gray-500 text-center">
                  <div className="flex flex-col items-center justify-center py-20">
                      <FaImages className="text-5xl text-gray-300 mb-4" />
                      <p className="text-xl text-gray-500 font-medium">
                        No Legal available.
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Once Legal are uploaded, they’ll show up here.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Image Enlarged View */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="relative"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ duration: 0.3 }}
              >
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-4 -right-4 bg-white p-1 rounded-full shadow hover:bg-gray-200"
                >
                  <IoMdClose className="text-black text-xl" />
                </button>
                <img
                  src={selectedImage}
                  alt="Enlarged"
                  className="max-w-[90vw] max-h-[80vh] rounded-xl shadow-2xl border-4 border-white"
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Pagination Controls */}
        {legals.length > 10 && (
          <div className="flex justify-center items-center mt-6 gap-2 flex-wrap">
            <button
              onClick={handlePrev}
              disabled={currentPage === 1}
              className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <FaChevronLeft size={14} />
            </button>

            {renderPageNumbers()}

            <button
              onClick={handleNext}
              disabled={currentPage === totalPages}
              className="p-2 rounded-full bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
            >
              <FaChevronRight size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListLegal;
